import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Tooltip,
  useTheme
} from '@mui/material';

export interface ImportOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  description?: string;
  disabled?: boolean;
  tooltip?: string;
}

interface ImportOptionCardProps {
  option: ImportOption;
  onClick: (optionId: string) => void;
  crmLogos?: Array<{ src: string; alt: string }>;
  showCRMLogos?: boolean;
  /** Hover border and icon color; defaults to brand primary. */
  interactionAccent?: string;
}

const ImportOptionCard: React.FC<ImportOptionCardProps> = ({
  option,
  onClick,
  crmLogos = [],
  showCRMLogos = false,
  interactionAccent
}) => {
  const theme = useTheme();
  const accent = interactionAccent ?? theme.customColors.themePrimary;

  const cardContent = (
    <Card
      onClick={() => !option.disabled && onClick(option.id)}
      sx={{
        height: '100%',
        cursor: option.disabled ? 'not-allowed' : 'pointer',
        border: '1px solid',
        borderColor: (theme) =>
          option.disabled ? theme.palette.divider : 'transparent',
        borderRadius: '12px',
        transition: 'all 0.2s ease',
        backgroundColor: (theme) =>
          option.disabled
            ? theme.palette.action.disabledBackground
            : theme.palette.background.paper,
        '&:hover': option.disabled
          ? {}
          : {
              borderColor: accent,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              transform: 'translateY(-2px)'
            }
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          py: 3,
          px: 2,
          textAlign: 'center'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            borderRadius: '12px',
            backgroundColor: (theme) =>
              option.disabled
                ? theme.palette.action.disabledBackground
                : theme.palette.action.hover,
            color: (theme) =>
              option.disabled ? theme.palette.action.disabled : accent,
            mb: 0.5
          }}
        >
          {option.id === 'import_crm' && showCRMLogos && crmLogos.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}
            >
              {crmLogos.map((logo, idx) => (
                <img
                  key={idx}
                  src={logo.src}
                  alt={logo.alt}
                  style={{
                    width: '24px',
                    height: '24px',
                    objectFit: 'contain'
                  }}
                />
              ))}
            </Box>
          ) : (
            option.icon
          )}
        </Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            fontSize: '15px',
            color: (theme) =>
              option.disabled
                ? theme.palette.text.disabled
                : theme.palette.text.primary
          }}
        >
          {option.label}
        </Typography>
        {option.description && (
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              color: (theme) =>
                option.disabled
                  ? theme.palette.text.disabled
                  : theme.palette.text.secondary,
              lineHeight: 1.4
            }}
          >
            {option.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (option.tooltip) {
    return (
      <Tooltip title={option.tooltip} arrow placement="top">
        <span>{cardContent}</span>
      </Tooltip>
    );
  }

  return cardContent;
};

export default ImportOptionCard;
